import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AppCard from '../components/AppCard';
import { API_URL } from '../config';
import '../styles/Home.css';

interface AppSummary {
  id: string;
  name: string;
  category: string;
  rating: number;
  price: number;
  icon: string;
  description: string;
}

const Home: React.FC = () => {
  const [apps, setApps] = useState<AppSummary[]>([]);
  const [filteredApps, setFilteredApps] = useState<AppSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  const filterBtnStyle = (isActive: boolean) => ({
    padding: '8px 16px',
    borderRadius: '20px',
    border: 'none',
    backgroundColor: isActive ? 'var(--primary-color)' : '#fff',
    color: isActive ? '#fff' : 'var(--text-primary)',
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: 'var(--shadow-subtle)',
    whiteSpace: 'nowrap' as const,
    transition: 'all 0.2s ease'
  });

  useEffect(() => {
    // Fetch apps (unrestricted)
    fetch(`${API_URL}/api/apps`)
      .then(res => res.json())
      .then(data => {
        setApps(data);
        setFilteredApps(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching apps:', err);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const term = searchParams.get('search')?.toLowerCase() || '';
    
    if (term) {
      setFilteredApps(apps.filter(app => 
        app.name.toLowerCase().includes(term) || 
        app.category.toLowerCase().includes(term)
      ));
    } else {
      setFilteredApps(apps);
    }
  }, [location.search, apps]);

  if (loading) {
    return (
      <div className="home-container">
        <div className="loading-state">Loading amazing sitters...</div>
      </div>
    );
  }

  return (
    <div className="home-container">
      <section className="hero">
        <h1>
          The <span style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.1)' }}>
            <span style={{ color: 'var(--pastel-pink)' }}>F</span>
            <span style={{ color: 'var(--pastel-blue)' }}>I</span>
            <span style={{ color: 'var(--pastel-purple)' }}>N</span>
            <span style={{ color: 'var(--pastel-green)' }}>D</span>
            .ca
          </span>
        </h1>
        <p>Your local network for finding certified, reliable junior sitters in your community.</p>
        <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', marginTop: '30px' }}>
          <button 
            onClick={() => navigate('/book/general')} 
            className="download-btn"
            style={{ padding: '15px 30px', fontSize: '1.1rem', backgroundColor: 'var(--primary-color)' }}
          >
            Quick Book Sitter 🚀
          </button>
        </div>
      </section>

      <div id="sitter-grid" className="category-filter" style={{ display: 'flex', gap: '10px', marginBottom: '30px', overflowX: 'auto', paddingBottom: '10px' }}>
        <button 
          onClick={() => navigate('/')} 
          className={`filter-btn ${!location.search ? 'active' : ''}`}
          style={filterBtnStyle(!location.search)}
        >
          All Sitters
        </button>
        {['Junior Sitter', 'Certified Sitter', 'Training'].map(cat => {
          const searchParams = new URLSearchParams(location.search);
          const currentSearch = searchParams.get('search') || '';
          const isActive = currentSearch.toLowerCase() === cat.toLowerCase();
          return (
            <button 
              key={cat}
              onClick={() => navigate(`/?search=${cat}`)} 
              className={`filter-btn ${isActive ? 'active' : ''}`}
              style={filterBtnStyle(isActive)}
            >
              {cat}
            </button>
          );
        })}
      </div>

      <div className="app-grid">
        {filteredApps?.length > 0 ? (
          filteredApps.map(app => (
            <AppCard key={app.id} {...app} />
          ))
        ) : (
          <div className="no-results">
            <h2>No sitters found matching your search.</h2>
            <button onClick={() => window.location.href = '/'} className="view-btn">Clear Search</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;
